import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { faTrafficLight, faSpinner, faArrowLeft, faPlus, faSort, faSortUp, faSortDown, faTimes } from '@/fontawesome';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import FullscreenError from '@/components/utils/FullscreenError';
import CreateStocksStatusLightModal from '@/components/admin/status-light/CreateStocksStatusLightModal.tsx';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { StocksStatusLight, StocksStatusLightColor, StocksStatusLightSort, SortDirection } from '@/models';

const pageSize = 50;

export default function AdminStocksStatusLightsList() {
  const { isAdmin } = useUser();
  const [lights, setLights] = useState<StocksStatusLight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [sortField, setSortField] = useState<StocksStatusLightSort>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC');
  const [colorFilter, setColorFilter] = useState<StocksStatusLightColor | null>(null);

  useEffect(() => {
    fetchLights();
  }, [currentPage, sortField, sortDirection, colorFilter]);

  const fetchLights = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listStocksStatusLights({
        limit: pageSize,
        next: cursorToken,
        sort: sortField,
        sortDirection: sortDirection,
        color: colorFilter ?? undefined
      });

      setLights(response.statusLights || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Critical error loading stocks status lights list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Börsen-Ampel Historie');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLightCreated = () => {
    setCurrentPage(1);
    fetchLights();
  };

  const handleSort = (field: StocksStatusLightSort) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('DESC');
    }
  };

  const getSortIcon = (field: StocksStatusLightSort) => {
    if (sortField !== field) {
      return faSort;
    }
    return sortDirection === 'ASC' ? faSortUp : faSortDown;
  };

  const getColorBadge = (color: StocksStatusLightColor) => {
    switch (color) {
      case 'GREEN':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-2" />
            Grün
          </span>
        );
      case 'YELLOW':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
            <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" />
            Gelb
          </span>
        );
      case 'RED':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            <div className="w-3 h-3 rounded-full bg-red-500 mr-2" />
            Rot
          </span>
        );
    }
  };


  if (criticalError) {
    return (
      <FullscreenError
        title="Börsen-Ampel Historie nicht verfügbar"
        message={error || 'Die Börsen-Ampel Historie konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchLights}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/admin"
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Börsen-Ampel Historie ({total})
          </h1>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          disabled={!isAdmin}
          title={isAdmin ? 'Ampel setzen' : 'Nur für Administratoren'}
          className={`bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 cursor-pointer'}`}
        >
          <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
          <span>Ampel setzen</span>
        </button>
      </div>

      {/* Filter */}
      <div className="mb-6 flex items-center space-x-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Filter:
        </label>
        <div className="flex items-center space-x-2">
          <select
            value={colorFilter ?? ''}
            onChange={(e) => {
              setColorFilter(e.target.value === '' ? null : e.target.value as StocksStatusLightColor);
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="">Alle Farben</option>
            <option value="GREEN">Nur Grün</option>
            <option value="YELLOW">Nur Gelb</option>
            <option value="RED">Nur Rot</option>
          </select>
          {colorFilter !== null && (
            <button
              onClick={() => {
                setColorFilter(null);
                setCurrentPage(1);
              }}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 cursor-pointer"
            >
              <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {error && !criticalError && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && lights.length === 0 && (
        <div className="text-center py-12">
          <FontAwesomeIcon
            icon={faTrafficLight}
            className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4"
          />
          <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
            Keine Einträge gefunden
          </h3>
          <p className="text-gray-500 dark:text-gray-500 mb-6">
            Setzen Sie die erste Börsen-Ampel.
          </p>
        </div>
      )}

      {!loading && lights.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Farbe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kommentar
                  </th>
                  <th
                    onClick={() => handleSort('created')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Erstellt</span>
                      <FontAwesomeIcon icon={getSortIcon('created')} className="w-3 h-3" />
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('updated')}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                  >
                    <div className="flex items-center space-x-1">
                      <span>Geändert</span>
                      <FontAwesomeIcon icon={getSortIcon('updated')} className="w-3 h-3" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {lights.map((light) => (
                  <tr key={light.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getColorBadge(light.color)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {light.comment}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {light.created !== null ? formatDate(light.created) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {light.updated !== null ? formatDate(light.updated) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PageBasedPagination
            currentPage={currentPage}
            totalCount={total}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}

      <CreateStocksStatusLightModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={handleLightCreated}
      />
    </>
  );
}
